// /keep-mowing/<id> — v7 rewrite.
// Adds:
//   - Approved photo strip when resident opted in + admin vetted keys
//   - CommitCalendar (visual month grid) for picking start date
//   - Falls back to the simpler commit form when calendar can't render
//     (e.g. user picks weekly cadence so the Saturday-only grid is moot).

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CommitCalendar } from '../components/CommitCalendar';

interface Crowdfund {
  id: number;
  status: string;
  bid_amount_cents: number;
  cycle_months: number;
  bid_notes: string | null;
  mowkin_label: string | null;
  mowkin_kind: 'volunteer' | 'provider' | null;
  resident_street: string;
  resident_city: string;
  resident_county: string;
  photos: string[];
}
interface Pledge {
  id: number; created_at: string;
  pledger_name: string | null; amount_cents: number;
  message: string | null; status: string;
}
interface Commit {
  id: number; cadence: string; custom_cadence: string | null;
  preferred_day: string | null; start_date: string; end_date: string | null;
  status: string;
}
interface MyVolunteer {
  id: number; first_name: string; last_name: string; status: string;
}

function fmtMoney(cents: number) { return `$${(cents / 100).toFixed(2)}`; }

export function KeepMowingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [data, setData] = useState<{
    crowdfund: Crowdfund;
    pledges: Pledge[];
    pledged_cents: number;
    commits: Commit[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pledge form
  const [pledgeDollars, setPledgeDollars] = useState('20');
  const [pledgerName, setPledgerName] = useState('');
  const [pledgerEmail, setPledgerEmail] = useState('');
  const [pledgeMessage, setPledgeMessage] = useState('');
  const [pledging, setPledging] = useState(false);
  const [pledgeMsg, setPledgeMsg] = useState<string | null>(null);

  // Commit form (volunteers only)
  const [myVolunteers, setMyVolunteers] = useState<MyVolunteer[]>([]);
  const [selectedVolunteerId, setSelectedVolunteerId] = useState<number | null>(null);
  const [cadence, setCadence] = useState('monthly');
  const [customCadence, setCustomCadence] = useState('');
  const [preferredDay, setPreferredDay] = useState('sat');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [commitNotes, setCommitNotes] = useState('');
  const [committing, setCommitting] = useState(false);
  const [commitMsg, setCommitMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`/api/crowdfund/${id}`);
      const j = await res.json() as { ok: true; data: typeof data } | { ok: false; error: string };
      if (j.ok) setData(j.data); else setError(j.error);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await fetch('/api/me/dashboard', { credentials: 'include' });
        const j = await res.json() as { ok: boolean; data?: { volunteers: MyVolunteer[] } };
        if (j.ok) {
          const vetted = (j.data?.volunteers || []).filter((v) => v.status === 'vetted');
          setMyVolunteers(vetted);
          if (vetted.length > 0) setSelectedVolunteerId(vetted[0].id);
        }
      } catch { /* ignore */ }
    })();
  }, [user]);

  useEffect(() => { void load(); }, [id]);

  const target = useMemo(
    () => data ? data.crowdfund.bid_amount_cents * data.crowdfund.cycle_months : 0,
    [data],
  );
  const pct = useMemo(
    () => data && target > 0 ? Math.min(100, Math.round((data.pledged_cents / target) * 100)) : 0,
    [data, target],
  );

  const submitPledge = async () => {
    setPledging(true); setPledgeMsg(null);
    try {
      const dollars = Number(pledgeDollars);
      if (!Number.isFinite(dollars) || dollars < 1) throw new Error('Pledge at least $1');
      const res = await fetch(`/api/crowdfund/${id}/pledge`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: Math.round(dollars * 100),
          pledger_name:  pledgerName || undefined,
          pledger_email: pledgerEmail || undefined,
          message: pledgeMessage || undefined,
        }),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Pledge failed');
      setPledgeMsg('Thank you. Your pledge is recorded. We will email when payment is collected.');
      setPledgeDollars('20'); setPledgerName(''); setPledgerEmail(''); setPledgeMessage('');
      void load();
    } catch (e) {
      setPledgeMsg(e instanceof Error ? e.message : 'Pledge failed');
    } finally { setPledging(false); }
  };

  const submitCommit = async () => {
    if (!selectedVolunteerId || !startDate) {
      setCommitMsg('Pick a volunteer record and a start date.');
      return;
    }
    setCommitting(true); setCommitMsg(null);
    try {
      const res = await fetch('/api/me/commit', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          crowdfund_id: Number(id),
          volunteer_id: selectedVolunteerId,
          cadence,
          custom_cadence: cadence === 'custom' ? customCadence : undefined,
          preferred_day: preferredDay,
          start_date: startDate,
          end_date: endDate || undefined,
          notes: commitNotes || undefined,
        }),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Commit failed');
      setCommitMsg('Thanks for signing up. We will confirm by email.');
      setCustomCadence(''); setCommitNotes('');
      void load();
    } catch (e) {
      setCommitMsg(e instanceof Error ? e.message : 'Commit failed');
    } finally { setCommitting(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-forest/60 italic">Loading...</div>;
  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="bg-rose/10 border-l-4 border-rose p-4 text-rose text-sm">{error || 'Not found.'}</div>
        <Link to="/keep-mowing" className="text-rose hover:underline mt-4 inline-block">← back to all households</Link>
      </div>
    );
  }

  const c = data.crowdfund;
  const isVetted = myVolunteers.length > 0;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
      <Link to="/keep-mowing" className="text-rose hover:underline text-sm">← all households</Link>

      <div className="text-xs tracking-[0.25em] uppercase text-rose mt-6 mb-3">
        {c.resident_city}, {c.resident_county}
      </div>
      <h1
        className="text-5xl text-forest mb-3 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        A household on {c.resident_street}.
      </h1>
      {c.mowkin_label && (
        <p className="text-forest/75 mb-2">
          Continuing care from <strong className="text-forest">{c.mowkin_label}</strong>
          {c.mowkin_kind && <span className="text-forest/55 text-sm"> · {c.mowkin_kind}</span>}
        </p>
      )}
      {c.bid_notes && (
        <p className="text-forest/65 italic border-l-2 border-forest/20 pl-3 my-4">{c.bid_notes}</p>
      )}

      {/* Approved photos */}
      {c.photos && c.photos.length > 0 && (
        <section className="mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {c.photos.map((key) => (
              <img
                key={key}
                src={`/api/public-photo/${encodeURIComponent(key)}`}
                alt="Yard photo"
                className="w-full h-32 object-cover border border-forest/15"
                loading="lazy"
              />
            ))}
          </div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-forest/45 mt-2">
            Shared by the household. Reviewed by our admin before display.
          </p>
        </section>
      )}

      {/* Progress card */}
      <section className="bg-cream border border-forest/15 p-6 mb-8">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-2xl text-forest font-display">{fmtMoney(data.pledged_cents)}</div>
          <div className="text-sm text-forest/55">
            of {fmtMoney(c.bid_amount_cents * c.cycle_months)} ·{' '}
            {c.cycle_months} month{c.cycle_months === 1 ? '' : 's'} of care
          </div>
        </div>
        <div className="bg-forest/5 h-3 mb-3 overflow-hidden">
          <div className="bg-rose h-full" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs uppercase tracking-[0.15em] text-forest/55">
          {data.pledges.length} pledge{data.pledges.length === 1 ? '' : 's'} so far
        </div>
      </section>

      {/* Pledge */}
      <section className="bg-cream border border-forest/15 p-6 mb-6">
        <h2 className="text-xl text-forest font-display mb-1">Pool funds</h2>
        <p className="text-sm text-forest/65 mb-4">Pledge what feels right. We collect when the goal is met.</p>

        <div className="mb-3">
          <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-1">Amount (USD)</div>
          <div className="flex items-center gap-2">
            <span className="text-forest/55">$</span>
            <input
              type="number" min={1} step={1}
              value={pledgeDollars} onChange={(e) => setPledgeDollars(e.target.value)}
              className="flex-1 border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <input
            type="text" value={pledgerName} onChange={(e) => setPledgerName(e.target.value)}
            placeholder="Your name (optional)"
            className="border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
          />
          <input
            type="email" value={pledgerEmail} onChange={(e) => setPledgerEmail(e.target.value)}
            placeholder="Email (optional)"
            className="border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
          />
        </div>
        <textarea
          rows={2} value={pledgeMessage} onChange={(e) => setPledgeMessage(e.target.value)}
          placeholder="Message (optional)"
          className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose mb-3"
        />
        <button
          type="button" disabled={pledging} onClick={submitPledge}
          className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-5 py-2 hover:bg-rose transition-colors disabled:opacity-50"
        >
          {pledging ? 'Sending...' : 'Pledge'}
        </button>
        {pledgeMsg && <p className="text-sm text-forest/75 mt-2">{pledgeMsg}</p>}
      </section>

      {/* Commit — with calendar */}
      <section className="bg-cream border border-forest/15 p-6 mb-10">
        <h2 className="text-xl text-forest font-display mb-1">Or — mow it yourself</h2>
        <p className="text-sm text-forest/65 mb-4">
          If you're a vetted volunteer, sign up for a recurring cadence. Click a Saturday below to start there.
        </p>
        {!user ? (
          <div className="text-sm text-forest/70">
            <Link to="/login" className="text-rose hover:underline">Sign in</Link> with a vetted volunteer account.
          </div>
        ) : !isVetted ? (
          <div className="text-sm text-forest/70">
            You need a vetted volunteer record to commit.{' '}
            <Link to="/volunteer" className="text-rose hover:underline">Apply to volunteer</Link>.
          </div>
        ) : (
          <>
            <div className="mb-5">
              <CommitCalendar
                commits={data.commits}
                monthsAhead={3}
                onClickWeek={(iso) => setStartDate(iso)}
              />
            </div>

            {myVolunteers.length > 1 && (
              <select
                value={selectedVolunteerId || ''}
                onChange={(e) => setSelectedVolunteerId(Number(e.target.value))}
                className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose mb-3"
              >
                {myVolunteers.map((v) => (
                  <option key={v.id} value={v.id}>{v.first_name} {v.last_name} (#{v.id})</option>
                ))}
              </select>
            )}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <select
                value={cadence} onChange={(e) => setCadence(e.target.value)}
                className="border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every other week</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom</option>
              </select>
              <select
                value={preferredDay} onChange={(e) => setPreferredDay(e.target.value)}
                className="border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
              >
                <option value="sun">Sundays</option>
                <option value="mon">Mondays</option>
                <option value="tue">Tuesdays</option>
                <option value="wed">Wednesdays</option>
                <option value="thu">Thursdays</option>
                <option value="fri">Fridays</option>
                <option value="sat">Saturdays</option>
              </select>
            </div>
            {cadence === 'custom' && (
              <input
                type="text" value={customCadence} onChange={(e) => setCustomCadence(e.target.value)}
                placeholder="e.g. first and third Saturday"
                className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose mb-3"
              />
            )}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-1">Start</div>
                <input
                  type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
                />
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-1">End (optional)</div>
                <input
                  type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
                />
              </div>
            </div>
            <textarea
              rows={2} value={commitNotes} onChange={(e) => setCommitNotes(e.target.value)}
              placeholder="Notes for admin (optional)"
              className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose mb-3"
            />
            <button
              type="button" disabled={committing} onClick={submitCommit}
              className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-5 py-2 hover:bg-rose transition-colors disabled:opacity-50"
            >
              {committing ? 'Sending...' : 'Sign up to mow this yard'}
            </button>
            {commitMsg && <p className="text-sm text-forest/75 mt-2">{commitMsg}</p>}
          </>
        )}
      </section>

      {/* Existing pledges */}
      {data.pledges.length > 0 && (
        <section>
          <h2 className="text-xl text-forest font-display mb-3">Pledges</h2>
          <div className="space-y-2">
            {data.pledges.map((p) => (
              <div key={p.id} className="bg-cream border border-forest/15 p-3 flex items-center gap-3 flex-wrap text-sm">
                <span className="text-forest font-medium">{fmtMoney(p.amount_cents)}</span>
                <span className="text-forest/75">{p.pledger_name || 'A neighbor'}</span>
                {p.message && <span className="text-forest/55 italic">— "{p.message}"</span>}
                <span className="ml-auto text-[10px] tracking-[0.15em] uppercase text-forest/55">{p.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
